import { sql } from 'drizzle-orm';
import { index, integer, sqliteTable, text } from 'drizzle-orm/sqlite-core';

// ---------------------------------------------------------------------------
// Better Auth tables (server-only).
// Property names follow Better Auth's documented Drizzle/SQLite schema
// (https://www.better-auth.com/docs/concepts/database#core-schema and
// https://www.better-auth.com/docs/adapters/drizzle). Table names are plural, so v0.1b must pass
// `usePlural: true` to drizzleAdapter. Timestamps are Date objects stored as integer milliseconds
// because Better Auth reads and writes Date values — the one exception to the ISO-text rule.
// ---------------------------------------------------------------------------

const nowMs = sql`(cast(unixepoch('subsecond') * 1000 as integer))`;

export const users = sqliteTable('users', {
  id: text('id').primaryKey(),
  name: text('name').notNull(),
  email: text('email').notNull().unique(),
  emailVerified: integer('email_verified', { mode: 'boolean' }).notNull().default(false),
  image: text('image'),
  createdAt: integer('created_at', { mode: 'timestamp_ms' }).notNull().default(nowMs),
  updatedAt: integer('updated_at', { mode: 'timestamp_ms' })
    .notNull()
    .default(nowMs)
    .$onUpdate(() => new Date()),
  /** 'user' | 'super_admin' */
  role: text('role', { enum: ['user', 'super_admin'] })
    .notNull()
    .default('user'),
  invitedBy: text('invited_by'),
});

export const sessions = sqliteTable(
  'sessions',
  {
    id: text('id').primaryKey(),
    expiresAt: integer('expires_at', { mode: 'timestamp_ms' }).notNull(),
    token: text('token').notNull().unique(),
    createdAt: integer('created_at', { mode: 'timestamp_ms' }).notNull().default(nowMs),
    updatedAt: integer('updated_at', { mode: 'timestamp_ms' })
      .notNull()
      .$onUpdate(() => new Date()),
    ipAddress: text('ip_address'),
    userAgent: text('user_agent'),
    userId: text('user_id')
      .notNull()
      .references(() => users.id, { onDelete: 'cascade' }),
  },
  (t) => [index('sessions_user_id_idx').on(t.userId)],
);

export const accounts = sqliteTable(
  'accounts',
  {
    id: text('id').primaryKey(),
    accountId: text('account_id').notNull(),
    providerId: text('provider_id').notNull(),
    userId: text('user_id')
      .notNull()
      .references(() => users.id, { onDelete: 'cascade' }),
    accessToken: text('access_token'),
    refreshToken: text('refresh_token'),
    idToken: text('id_token'),
    accessTokenExpiresAt: integer('access_token_expires_at', { mode: 'timestamp_ms' }),
    refreshTokenExpiresAt: integer('refresh_token_expires_at', { mode: 'timestamp_ms' }),
    scope: text('scope'),
    password: text('password'),
    createdAt: integer('created_at', { mode: 'timestamp_ms' }).notNull().default(nowMs),
    updatedAt: integer('updated_at', { mode: 'timestamp_ms' })
      .notNull()
      .$onUpdate(() => new Date()),
  },
  (t) => [index('accounts_user_id_idx').on(t.userId)],
);

export const verifications = sqliteTable(
  'verifications',
  {
    id: text('id').primaryKey(),
    identifier: text('identifier').notNull(),
    value: text('value').notNull(),
    expiresAt: integer('expires_at', { mode: 'timestamp_ms' }).notNull(),
    createdAt: integer('created_at', { mode: 'timestamp_ms' }).notNull().default(nowMs),
    updatedAt: integer('updated_at', { mode: 'timestamp_ms' })
      .notNull()
      .default(nowMs)
      .$onUpdate(() => new Date()),
  },
  (t) => [index('verifications_identifier_idx').on(t.identifier)],
);

// ---------------------------------------------------------------------------
// Tahi sign-in tables (server-only). ISO-8601 UTC text timestamps.
// ---------------------------------------------------------------------------

/**
 * Emails allowed to sign up while app_settings.invite_only is on. Email is stored lower-case.
 * Signing up takes the one-time link's token too (valid 7 days), so knowing an invited email is not enough.
 */
export const invites = sqliteTable('invites', {
  id: text('id').primaryKey(),
  email: text('email').notNull().unique(),
  /** Null only for invites created by the seed script. */
  invitedBy: text('invited_by').references(() => users.id),
  note: text('note'),
  createdAt: text('created_at').notNull(),
  acceptedAt: text('accepted_at'),
  acceptedUserId: text('accepted_user_id').references(() => users.id),
  revokedAt: text('revoked_at'),
  /** SHA-256 of the token in the invite link; the raw token is never stored. Null = no working link. */
  tokenHash: text('token_hash').unique(),
  expiresAt: text('expires_at'),
});

/** One-time password reset links made by a super admin (valid 1 hour, single use). */
export const passwordResetLinks = sqliteTable(
  'password_reset_links',
  {
    id: text('id').primaryKey(),
    userId: text('user_id')
      .notNull()
      .references(() => users.id, { onDelete: 'cascade' }),
    /** SHA-256 of the token in the link; the raw token is never stored. */
    tokenHash: text('token_hash').notNull().unique(),
    createdBy: text('created_by')
      .notNull()
      .references(() => users.id),
    createdAt: text('created_at').notNull(),
    expiresAt: text('expires_at').notNull(),
    usedAt: text('used_at'),
  },
  (t) => [index('password_reset_links_user_id_idx').on(t.userId)],
);

/**
 * Every sign-in attempt; 5 failures in 15 minutes lock that email and IP (an IPv6 /64), and 20 from
 * any IPs lock the email, for 15 minutes. Change-password checks are logged as email `user:<id>`.
 */
export const loginAttempts = sqliteTable(
  'login_attempts',
  {
    id: text('id').primaryKey(),
    email: text('email').notNull(),
    ip: text('ip').notNull(),
    success: integer('success', { mode: 'boolean' }).notNull(),
    at: text('at').notNull(),
  },
  (t) => [
    index('login_attempts_email_at_idx').on(t.email, t.at),
    index('login_attempts_ip_at_idx').on(t.ip, t.at),
  ],
);
